<template>
  <template v-if="menuItem.level <= 1">
    <el-sub-menu
      v-if="hasChild"
      :index="menuItem.menuId"
      popperClass="hide-menu-popper"
      :popperVisible="isCollapse ? subMenuPopperVisible : false"
    >
      <template #title>
        <div class="menu-content">
          <template v-if="menuItem.menuIcon">
            <img
              v-if="iconUrl(menuItem)"
              :src="iconUrl(menuItem)"
              alt=""
              width="18"
              height="18"
              class="iconfont"
            />
            <i v-else :class="[menuItem.menuIcon]"></i>
          </template>
          <span class="menu-name" :style="menuNameStyle" :title="$t(menuItem.menuName)">
            {{ $t(menuItem.menuName) }}
          </span>
        </div>
      </template>
      <template v-for="item in menuItem.childMenuList" :key="item.menuRouteUrl">
        <MenuItem
          :menuItem="item"
          :is-collapse="isCollapse"
          @updatePopperVisible="updatePopperVisible"
        />
      </template>
    </el-sub-menu>
    <el-menu-item
      v-else
      :index="menuItem.menuRouteUrl"
      class="level-1-item"
      @mouseover="handleChildrenOver(true)"
      @mouseenter="handleChildrenOver(true)"
      @mouseleave="handleChildrenOver(false)"
    >
      <template v-if="menuItem.menuIcon">
        <img
          v-if="iconUrl(menuItem)"
          :src="iconUrl(menuItem)"
          alt=""
          width="18"
          height="18"
          class="iconfont"
        />
        <i v-else :class="[menuItem.menuIcon]"></i>
      </template>
      <template #title>
        <div class="menu-content">
          <div class="menu-name" :style="menuNameStyle" :title="$t(menuItem.menuName)">
            {{ $t(menuItem.menuName) }}
          </div>
          <div class="new-menu" v-if="menuItem.isNew">NEW</div>
          <MenuCollect
            :menuItem="menuItem"
            :class="['icon-star', getCollectStatus(menuItem) ? 'collect-item' : null]"
          />
        </div>
      </template>
    </el-menu-item>
  </template>
  <el-popover
    v-model:visible="childVisible"
    :disabled="!hasChild || menuItem.level !== 2"
    :placement="isCollapse ? 'right' : 'right-start'"
    :popper-class="`menu-children ${isCollapse ? 'hide-menu-children' : ''}`"
    trigger="hover"
    :show-arrow="false"
    :offset="1"
    width="800"
    :key="popoverKey"
    v-else
  >
    <div
      class="menu-children-container"
      @mouseover="handleChildrenOver(true)"
      @mouseenter="handleChildrenOver(true)"
      @mouseleave="handleChildrenOver(false)"
      :style="{
        maxWidth: popoverWidth
      }"
      ref="menuChildrenContainerRef"
    >
      <MenuChildren
        v-for="(item, index) in childMenu"
        :key="index"
        :menuItem="item"
        :style="{
          marginbottom: hasChild ? '28px' : 0,
          width: `${colWidth}px`,
          marginLeft: `${colPadding}px`,
          marginRight: `${colPadding}px`
        }"
        @menuClick="childVisible = false"
      ></MenuChildren>
      <div
        class="col-line"
        v-for="value in colsNum - 1"
        :key="value"
        :style="{
          left: `${value * colWidth + value * colPadding * 2}px`
        }"
      ></div>
    </div>
    <template #reference>
      <div class="menu-item-box">
        <el-menu-item
          class="sub-menu-item"
          :class="{
            'has-route': !!menuItem.menuRouteUrl
          }"
          :index="
            (hasChild ? `${SUBMENU_PREFIX}-${menuItem.menuId}` : menuItem.menuRouteUrl) ||
            `${SUBMENU_PREFIX}-${menuItem.menuId}`
          "
        >
          <template v-if="menuItem.menuIcon">
            <img
              v-if="iconUrl(menuItem)"
              :src="iconUrl(menuItem)"
              alt=""
              width="18"
              height="18"
              class="iconfont"
            />
            <i v-else :class="[menuItem.menuIcon]"></i>
          </template>
          <template #title>
            <div class="menu-name" :style="menuNameStyle" :title="$t(menuItem.menuName)">
              {{ $t(menuItem.menuName) }}
            </div>
            <div class="new-menu" v-if="menuItem.isNew && !hasChild">NEW</div>
            <MenuCollect
              v-if="!hasChild"
              :menuItem="menuItem"
              :class="['icon-star', getCollectStatus(menuItem) ? 'collect-item' : null]"
            />
          </template>
          <template v-if="hasChild && menuItem.level === 2">
            <i class="el-sub-menu__icon-arrow el-icon-arrow-down sub-menu-item-icon"></i>
          </template>
        </el-menu-item>
      </div>
    </template>
  </el-popover>
</template>

<script>
import { defineComponent } from 'vue'
import ElSubMenu from '@/components/el/menu/submenu.vue'
import ElMenuItem from '@/components/el/menu/menuItem.vue'
import { $t, isCN } from '@/lang/index'
import { SUBMENU_PREFIX } from './constants'
import MenuChildren from './MenuChildren.vue'
import { debounce, uniqueId } from 'lodash'
import MenuCollect from '@/components/Layout/MenuCollect.vue'

export default defineComponent({
  name: 'MenuItem',
  data() {
    return {
      iconMap: {
        main_menu_lct: 'lct_icon.svg',
        物流控制塔: 'lct_icon.svg',
        网络规划管理: 'network_icon.svg'
      },
      SUBMENU_PREFIX,
      childVisible: false,
      subMenuPopperVisible: false,
      resizeObserver: undefined,
      menuChildrenContainerWidth: 0,
      colWidth: 208,
      colPadding: 24
    }
  },
  props: {
    menuItem: Object,
    isCollapse: {
      type: Boolean,
      default: false
    }
  },
  emits: ['updatePopperVisible'],
  inject: ['containerProvide'],
  components: {
    ElSubMenu,
    ElMenuItem,
    MenuChildren,
    MenuCollect
  },
  computed: {
    menuNameStyle() {
      return {
        fontSize: isCN.value ? '14px' : '12px',
        maxWidth: `calc(100% - ${this.menuItem.isNew ? '30px' : 0} - ${
          !this.hasChild ? '20px' : 0
        })`
      }
    },
    /**
     * 是否包含子节点
     */
    hasChild() {
      return this.menuItem.childMenuList?.length > 0
    },
    childMenu() {
      return this.menuItem?.childMenuList
    },
    popoverKey() {
      return this.isCollapse + uniqueId('MenuItem')
    },
    popoverWidth() {
      return this.containerProvide.containerWidth.value
    },
    /**
     * 列数
     */
    colsNum() {
      const availableWidth = this.menuChildrenContainerWidth
      return Math.max(1, Math.ceil(availableWidth / (this.colWidth + this.colPadding * 2)))
    }
  },
  methods: {
    iconUrl(item) {
      const isHttpUrl = item.menuIcon.indexOf('http') !== -1
      if (isHttpUrl) {
        return item.menuIcon
      } else if (this.iconMap[item.menuName]) {
        return require('@/assets/define-icon/' + this.iconMap[item.menuName])
      } else {
        return null
      }
    },
    handleChildrenOver(open) {
      this.$emit('updatePopperVisible', open)
    },
    updatePopperVisible(val) {
      if (val) {
        this.subMenuPopperVisible = val
      } else {
        this.closeMenu()
      }
    },
    closeMenu: debounce(function () {
      this.subMenuPopperVisible = false
    }, 300),
    handleResize() {
      if (this.$refs.menuChildrenContainerRef) {
        this.menuChildrenContainerWidth = this.$refs.menuChildrenContainerRef.scrollWidth
      }
    },
    getCollectStatus(item) {
      return this.$store.state.menu.collectList.some(menu => menu.menuId === item.menuId)
    }
  },
  mounted() {
    if (this.$refs.menuChildrenContainerRef) {
      this.resizeObserver = new ResizeObserver(this.handleResize)
      this.resizeObserver.observe(this.$refs.menuChildrenContainerRef)
    }
  },
  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  }
})
</script>

<style lang="scss" scoped>
.el-sub-menu,
.level-1-item {
  .iconfont {
    margin-right: 8px;
    width: 18px;
    text-align: center;
    font-size: 18px;
    vertical-align: middle;
    margin-bottom: 2px;
  }
}

.level-1-item {
  padding-right: 24px;
  .menu-content {
    flex: 1;
    overflow: hidden;
  }
  &.is-active {
    border-color: #e2e8fa;
    background-color: #f4f7ff;
    color: #1d4cd2;
  }
  &:hover {
    .icon-star {
      opacity: 1;
    }
  }
}

.menu-content,
.level-1-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 44px;
  line-height: 44px;
}
.menu-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
}

:deep(.el-sub-menu) {
  .el-menu-item {
    padding-right: 20px;
  }
  &:hover {
    background-color: red;
    & > .el-sub-menu__title {
      background-color: #f4f7ff;
      color: #1d4cd2;
    }
  }
}

.menu-item-box {
  padding: 4px 8px;
  position: relative;
  .sub-menu-item-icon {
    transform: rotate(-90deg);
    margin-right: -8px;
  }
  .sub-menu-item {
    height: 36px;
    line-height: 36px;
    border-radius: 8px;
    border: 1px solid transparent;
    min-width: 192px;
    transition: none;
    padding-right: 25px;
    display: flex;
    flex-direction: row;
    align-items: center;
    &.is-active.has-route {
      border-color: #e2e8fa;
      background-color: #f4f7ff;
      color: #1d4cd2;
      .sub-menu-item-icon {
        /* opacity: 1; */
        color: #1d4cd2;
      }
    }
  }
  .icon-star {
    right: 0;
  }
  &:hover {
    .sub-menu-item {
      border-color: #e2e8fa;
      background-color: #f4f7ff;
      color: #1d4cd2;
    }
    .sub-menu-item-icon {
      color: #1d4cd2;
    }
    .icon-star {
      opacity: 1;
    }
  }
}

.icon-star {
  opacity: 0;
  position: absolute;
  top: 0;
  right: 8px;
  &.collect-item {
    opacity: 1;
  }
}

:deep(.new-menu) {
  color: rgba(3, 156, 115, 1);
  font-size: 12px;
  flex-shrink: 0;
  margin-left: 6px;
}
</style>

<style lang="scss">
.menu-children {
  height: calc(100vh - 2px - 82px);
  box-sizing: border-box;
  padding: 0 !important;
  &.hide-menu-children {
    max-height: calc(100vh - 2px - 82px);
    overflow-y: auto;
    height: auto;
    top: auto;
  }
  &.el-popper.is-light {
    box-shadow: 0px 4px 13px rgba(0, 0, 0, 0.1), 0px 17.6944px 42.875px rgba(0, 0, 0, 0.03);
  }
}
.layout-container {
  .el-menu {
    .el-menu-item.is-active {
      border-color: #e2e8fa;
      background-color: #f4f7ff;
      color: #1d4cd2;
      box-shadow: none;
    }
  }
}
.menu-children-container {
  padding: 40px 0 !important;
  box-sizing: border-box;
  height: 100%;
  z-index: 99;
  position: relative;
  overflow-x: auto;
  background: #fff;
  max-width: 950px;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: flex-start;
  align-content: flex-start;
  flex-direction: column;
  & > .menu-children-item {
    margin-bottom: 28px;
  }
  .col-line {
    position: absolute;
    top: 24px;
    height: calc(100% - 24px - 24px);
    background-color: rgba(238, 238, 238, 1);
    width: 1px;
    left: 0;
  }
}
.hide-menu-popper {
  .el-menu--popup {
    max-height: calc(90vh);
    overflow-y: auto;
    transform: translateZ(0);
  }
}
</style>
